import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormInput } from '../components/checkin/FormInput';
import { FormSelect } from '../components/checkin/FormSelect';
import {
  HotelEnriched,
  HotelEnrichedErrors,
  createEmptyHotelEnriched,
} from '../types/hotelEnriched';
import {
  validateHotelEnrichedField,
  validateHotelEnrichedForm,
} from '../utils/hotelEnrichedValidation';
import {
  PROVINCES_RDC,
  STAR_CATEGORIES,
  LEGAL_FORMS,
  ESTABLISHMENT_TYPES,
  PAYMENT_METHODS_OPTIONS,
} from '../utils/referenceData';
import { hotelEnrichedService } from '../services/hotelEnrichedService';

// Get hotelId from localStorage (set at login)
function getHotelId(): string | null {
  // First try direct localStorage (set at login)
  const stored = localStorage.getItem('hotelId');
  if (stored) return stored;
  // Fallback: decode JWT
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.hotel_id || null;
  } catch {
    return null;
  }
}

export default function HotelEnrichedFormPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<HotelEnriched>(createEmptyHotelEnriched());
  const [errors, setErrors] = useState<HotelEnrichedErrors>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const hotelId = getHotelId();

  // Load existing data in edit mode (Exigence 6.2)
  useEffect(() => {
    if (!hotelId) {
      setLoading(false);
      setGlobalError('Identifiant hôtel introuvable. Veuillez vous déconnecter puis vous reconnecter.');
      return;
    }
    setIsEditMode(true);
    hotelEnrichedService
      .getEnriched(hotelId)
      .then((data) => {
        setFormData({ ...createEmptyHotelEnriched(), ...data });
      })
      .catch(() => {
        // Hotel may not have enriched data yet — start with empty form
      })
      .finally(() => setLoading(false));
  }, [hotelId]);

  const handleChange = useCallback(
    (field: keyof HotelEnriched, value: any) => {
      setFormData((prev) => {
        const updated = { ...prev, [field]: value };
        // Validate on change
        const err = validateHotelEnrichedField(field, value, updated);
        setErrors((prevErr) => {
          if (err) return { ...prevErr, [field]: err };
          const { [field]: _, ...rest } = prevErr;
          return rest;
        });
        return updated;
      });
    },
    []
  );

  const handleNumberChange = useCallback(
    (field: keyof HotelEnriched, value: string) => {
      const num = value === '' ? (field === 'star_category' || field === 'total_rooms' || field === 'latitude' || field === 'longitude' ? null : 0) : Number(value);
      handleChange(field, num);
    },
    [handleChange]
  );

  const handleBoolChange = useCallback(
    (field: keyof HotelEnriched, checked: boolean) => {
      handleChange(field, checked);
    },
    [handleChange]
  );

  const handlePaymentMethodToggle = useCallback((method: string) => {
    setFormData((prev) => {
      const methods = prev.payment_methods.includes(method)
        ? prev.payment_methods.filter((m) => m !== method)
        : [...prev.payment_methods, method];
      return { ...prev, payment_methods: methods };
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGlobalError(null);
    setSuccessMsg(null);

    const validationErrors = validateHotelEnrichedForm(formData);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      setGlobalError('Veuillez corriger les erreurs dans le formulaire avant de soumettre.');
      return;
    }

    if (!hotelId) {
      setGlobalError('Identifiant hôtel introuvable. Veuillez vous reconnecter.');
      return;
    }

    setSaving(true);
    try {
      await hotelEnrichedService.updateEnriched(hotelId, formData);
      setSuccessMsg('Fiche hôtel enrichie enregistrée avec succès.');
      setTimeout(() => setSuccessMsg(null), 5000);
    } catch (err: any) {
      setGlobalError(err.message || 'Erreur lors de l\'enregistrement.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        <span className="ml-4 text-gray-600">Chargement de la fiche hôtel...</span>
      </div>
    );
  }

  const provinceOptions = PROVINCES_RDC.map((p) => ({ value: p, label: p }));

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Modifier la Fiche Hôtel' : 'Créer la Fiche Hôtel'}
          </h1>
          <p className="text-gray-600 mt-1">
            Remplissez tous les blocs pour compléter la fiche enrichie de l'établissement.
          </p>
        </div>
      </div>

      {successMsg && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {successMsg}
        </div>
      )}
      {globalError && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {globalError}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        {/* BLOC 1: Identification (Exigence 3.1) */}
        <Section title="Identification">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput label="Nom de l'hôtel" name="name" value={formData.name} onChange={(v) => handleChange('name', v)} error={errors.name} required />
            <FormInput label="Nom commercial" name="commercial_name" value={formData.commercial_name} onChange={(v) => handleChange('commercial_name', v)} />
            <FormInput label="Raison sociale" name="legal_name" value={formData.legal_name} onChange={(v) => handleChange('legal_name', v)} />
            <FormSelect label="Type d'établissement" name="establishment_type" value={formData.establishment_type} onChange={(v) => handleChange('establishment_type', v)} options={ESTABLISHMENT_TYPES} placeholder="Sélectionner..." />
            <FormSelect label="Catégorie (étoiles)" name="star_category" value={formData.star_category !== null ? String(formData.star_category) : ''} onChange={(v) => handleNumberChange('star_category', v)} options={STAR_CATEGORIES} error={errors.star_category} placeholder="Sélectionner..." />
            <FormSelect label="Forme juridique" name="legal_form" value={formData.legal_form} onChange={(v) => handleChange('legal_form', v)} options={LEGAL_FORMS} placeholder="Sélectionner..." />
            <FormInput label="N° Agrément Tourisme" name="tourism_approval_number" value={formData.tourism_approval_number} onChange={(v) => handleChange('tourism_approval_number', v)} />
            <FormInput label="RCCM" name="rccm" value={formData.rccm} onChange={(v) => handleChange('rccm', v)} />
            <FormInput label="NIF" name="nif" value={formData.nif} onChange={(v) => handleChange('nif', v)} />
            <FormInput label="CNSS" name="cnss" value={formData.cnss} onChange={(v) => handleChange('cnss', v)} />
            <FormInput label="Email" name="email" type="email" value={formData.email} onChange={(v) => handleChange('email', v)} error={errors.email} />
          </div>
        </Section>

        {/* BLOC 2: Localisation (Exigence 3.2) */}
        <Section title="Localisation">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormSelect label="Province" name="province" value={formData.province} onChange={(v) => handleChange('province', v)} options={provinceOptions} placeholder="Sélectionner une province..." />
            <FormInput label="Ville" name="city" value={formData.city} onChange={(v) => handleChange('city', v)} />
            <FormInput label="Commune" name="commune" value={formData.commune} onChange={(v) => handleChange('commune', v)} />
            <FormInput label="Quartier" name="quartier" value={formData.quartier} onChange={(v) => handleChange('quartier', v)} />
            <div className="md:col-span-2">
              <FormInput label="Adresse complète" name="full_address" value={formData.full_address} onChange={(v) => handleChange('full_address', v)} />
            </div>
            <FormInput label="Latitude" name="latitude" type="number" value={formData.latitude !== null ? String(formData.latitude) : ''} onChange={(v) => handleNumberChange('latitude', v)} error={errors.latitude} placeholder="-4.3250" />
            <FormInput label="Longitude" name="longitude" type="number" value={formData.longitude !== null ? String(formData.longitude) : ''} onChange={(v) => handleNumberChange('longitude', v)} error={errors.longitude} placeholder="15.3222" />
          </div>
        </Section>

        {/* BLOC 3: Gestion et Contacts (Exigence 3.3) */}
        <Section title="Gestion et Contacts">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <h4 className="md:col-span-3 text-sm font-semibold text-gray-500 uppercase">Propriétaire</h4>
            <FormInput label="Nom" name="owner_name" value={formData.owner_name} onChange={(v) => handleChange('owner_name', v)} />
            <FormInput label="Téléphone" name="owner_phone" type="tel" value={formData.owner_phone} onChange={(v) => handleChange('owner_phone', v)} error={errors.owner_phone} />
            <div /> {/* spacer */}
            <h4 className="md:col-span-3 text-sm font-semibold text-gray-500 uppercase mt-2">Gérant</h4>
            <FormInput label="Nom" name="manager_name" value={formData.manager_name} onChange={(v) => handleChange('manager_name', v)} />
            <FormInput label="Téléphone" name="manager_phone" type="tel" value={formData.manager_phone} onChange={(v) => handleChange('manager_phone', v)} error={errors.manager_phone} />
            <FormInput label="Email" name="manager_email" type="email" value={formData.manager_email} onChange={(v) => handleChange('manager_email', v)} error={errors.manager_email} />
            <h4 className="md:col-span-3 text-sm font-semibold text-gray-500 uppercase mt-2">Responsable opérationnel</h4>
            <FormInput label="Nom" name="operational_manager_name" value={formData.operational_manager_name} onChange={(v) => handleChange('operational_manager_name', v)} />
            <FormInput label="Téléphone" name="operational_manager_phone" type="tel" value={formData.operational_manager_phone} onChange={(v) => handleChange('operational_manager_phone', v)} error={errors.operational_manager_phone} />
            <FormInput label="Email" name="operational_manager_email" type="email" value={formData.operational_manager_email} onChange={(v) => handleChange('operational_manager_email', v)} error={errors.operational_manager_email} />
          </div>
        </Section>

        {/* BLOC 4: Exploitation (Exigence 3.4) */}
        <Section title="Exploitation">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <FormInput label="Nombre de chambres" name="total_rooms" type="number" value={formData.total_rooms !== null ? String(formData.total_rooms) : ''} onChange={(v) => handleNumberChange('total_rooms', v)} error={errors.total_rooms} />
            <FormInput label="Nombre de suites" name="num_suites" type="number" value={String(formData.num_suites)} onChange={(v) => handleNumberChange('num_suites', v)} error={errors.num_suites} />
            <FormInput label="Nombre de lits" name="num_beds" type="number" value={String(formData.num_beds)} onChange={(v) => handleNumberChange('num_beds', v)} error={errors.num_beds} />
            <FormInput label="Salles de conférence" name="num_conference_rooms" type="number" value={String(formData.num_conference_rooms)} onChange={(v) => handleNumberChange('num_conference_rooms', v)} error={errors.num_conference_rooms} />
          </div>
        </Section>

        {/* BLOC 5: Services/Commodités (Exigence 3.5) */}
        <Section title="Loisirs / Services / Commodités">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <BoolToggle label="Parking" checked={formData.has_parking} onChange={(v) => handleBoolChange('has_parking', v)} />
            <BoolToggle label="Piscine" checked={formData.has_pool} onChange={(v) => handleBoolChange('has_pool', v)} />
            <BoolToggle label="Salle de sport" checked={formData.has_gym} onChange={(v) => handleBoolChange('has_gym', v)} />
            <BoolToggle label="WiFi" checked={formData.has_wifi} onChange={(v) => handleBoolChange('has_wifi', v)} />
            <BoolToggle label="Restaurant" checked={formData.has_restaurant} onChange={(v) => handleBoolChange('has_restaurant', v)} />
            <BoolToggle label="Bar" checked={formData.has_bar} onChange={(v) => handleBoolChange('has_bar', v)} />
            <BoolToggle label="Climatisation" checked={formData.has_ac} onChange={(v) => handleBoolChange('has_ac', v)} />
            <BoolToggle label="Service en chambre" checked={formData.has_room_service} onChange={(v) => handleBoolChange('has_room_service', v)} />
            <BoolToggle label="Générateur" checked={formData.has_generator} onChange={(v) => handleBoolChange('has_generator', v)} />
            <BoolToggle label="Vidéosurveillance" checked={formData.has_cctv} onChange={(v) => handleBoolChange('has_cctv', v)} />
          </div>
        </Section>

        {/* BLOC 6: Ressources Humaines (Exigence 3.6) */}
        <Section title="Ressources Humaines">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormInput label="Salariés hommes" name="male_employees" type="number" value={String(formData.male_employees)} onChange={(v) => handleNumberChange('male_employees', v)} error={errors.male_employees} />
            <FormInput label="Salariés femmes" name="female_employees" type="number" value={String(formData.female_employees)} onChange={(v) => handleNumberChange('female_employees', v)} error={errors.female_employees} />
            <FormInput label="Agents de sécurité" name="security_agents" type="number" value={String(formData.security_agents)} onChange={(v) => handleNumberChange('security_agents', v)} error={errors.security_agents} />
            <FormInput label="Responsable RH (nom)" name="hr_manager_name" value={formData.hr_manager_name} onChange={(v) => handleChange('hr_manager_name', v)} />
            <FormInput label="Responsable RH (tél)" name="hr_manager_phone" type="tel" value={formData.hr_manager_phone} onChange={(v) => handleChange('hr_manager_phone', v)} error={errors.hr_manager_phone} />
          </div>
        </Section>

        {/* BLOC 7: Fiscalité (Exigence 3.7) */}
        <Section title="Fiscalité et Commerce">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3">
              <BoolToggle label="Assujetti FPT" checked={formData.is_fpt_subject} onChange={(v) => handleBoolChange('is_fpt_subject', v)} />
            </div>
            {formData.is_fpt_subject && (
              <FormInput label="Taux FPT (%)" name="fpt_rate" type="number" value={String(formData.fpt_rate)} onChange={(v) => handleNumberChange('fpt_rate', v)} error={errors.fpt_rate} />
            )}
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Moyens de paiement acceptés</label>
            <div className="flex flex-wrap gap-2">
              {PAYMENT_METHODS_OPTIONS.map((pm) => (
                <button
                  key={pm.value}
                  type="button"
                  onClick={() => handlePaymentMethodToggle(pm.value)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition ${
                    formData.payment_methods.includes(pm.value)
                      ? 'bg-blue-100 border-blue-400 text-blue-800'
                      : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {pm.label}
                </button>
              ))}
            </div>
          </div>
        </Section>

        {/* BLOC 8: Sécurité et Conformité (Exigence 3.8) */}
        <Section title="Sécurité et Conformité">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <BoolToggle label="Registre client actif" checked={formData.has_client_register} onChange={(v) => handleBoolChange('has_client_register', v)} />
            <BoolToggle label="Fiche de police activée" checked={formData.has_police_form} onChange={(v) => handleBoolChange('has_police_form', v)} />
            <BoolToggle label="Scanner document d'identité" checked={formData.has_id_scanner} onChange={(v) => handleBoolChange('has_id_scanner', v)} />
            <BoolToggle label="Contrôle d'accès" checked={formData.has_access_control} onChange={(v) => handleBoolChange('has_access_control', v)} />
            <BoolToggle label="Journal des visiteurs" checked={formData.has_visitor_log} onChange={(v) => handleBoolChange('has_visitor_log', v)} />
          </div>
        </Section>

        {/* BLOC 9: Métadonnées (Exigence 3.9) */}
        {isEditMode && formData.data_source && (
          <Section title="Métadonnées">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
              <div>
                <span className="font-medium">Source des données :</span>{' '}
                {formData.data_source === 'imported' ? 'Importé' : formData.data_source === 'test' ? 'Test' : 'Saisie manuelle'}
              </div>
              {formData.registered_at && (
                <div>
                  <span className="font-medium">Créé le :</span>{' '}
                  {new Date(formData.registered_at).toLocaleDateString('fr-FR')}
                </div>
              )}
              {formData.updated_at && (
                <div>
                  <span className="font-medium">Modifié le :</span>{' '}
                  {new Date(formData.updated_at).toLocaleDateString('fr-FR')}
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Submit */}
        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Enregistrement...
              </>
            ) : (
              'Enregistrer la fiche'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

// --- Helper components ---

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <div className="px-6 py-3 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="px-6 py-4">{children}</div>
    </div>
  );
}

function BoolToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center space-x-2 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
      />
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  );
}
